"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, BookOpen, Building2, Calendar, FileText, UserCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
interface UnitData {
  id: string;
  name: string;
  leaderTitle: string;
  leaderName: string;
  legalDecision: string;
  regCode: string;
  description: string;
  focusArea: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const researchUnits: UnitData[] = [{
  id: "cegtes",
  name: "Trung tâm Khoa học công nghệ Địa lý Môi trường Việt Nam (CEGTES)",
  leaderTitle: "Giám đốc",
  leaderName: "TS. Đỗ Ngọc Chung",
  legalDecision: "Quyết định 121/QĐ-VAEG ngày 07/11/2022",
  regCode: "A-2547",
  description: "Nghiên cứu ứng dụng công nghệ địa lý, đánh giá tác động biến đổi khí hậu và chuyển giao các giải pháp năng lượng sạch, bảo vệ môi trường sinh thái.",
  focusArea: "Năng lượng sạch & Ứng phó biến đổi khí hậu",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cegtes_clean_energy_project.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cegtes_clean_energy_project.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cegtes_clean_energy_project.png-wvc-sizes",
    alt: "Dự án nghiên cứu năng lượng sạch và thích ứng biến đổi khí hậu của CEGTES"
  }
}, {
  id: "ieg",
  name: "Viện Địa lý môi trường (IEG)",
  leaderTitle: "Viện trưởng",
  leaderName: "Phạm Hồng Hải",
  legalDecision: "Quyết định 08/QĐ-VAEG ngày 10/3/2023",
  regCode: "A-2646",
  description: "Tổ chức khảo sát thực địa, xây dựng cơ sở dữ liệu bản đồ không gian và phân tích hiện trạng các hệ sinh thái tài nguyên thiên nhiên.",
  focusArea: "Khảo sát thực địa & Bản đồ học môi trường",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/ieg_field_survey_activity.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/ieg_field_survey_activity.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/ieg_field_survey_activity.png-wvc-sizes",
    alt: "Hoạt động khảo sát thực địa và phân tích địa lý môi trường của Viện IEG"
  }
}, {
  id: "cvse",
  name: "Trung tâm nghiên cứu giá trị văn hóa - tôn giáo vì môi trường phát triển bền vững",
  leaderTitle: "Giám đốc",
  leaderName: "Nguyễn Thị Tuyết Mai",
  legalDecision: "Quyết định 01/QĐ-VAEG ngày 02/6/2025",
  regCode: "A-2865",
  description: "Bảo tồn và phát huy các giá trị văn hóa bản địa, tín ngưỡng truyền thống trong việc quản lý tài nguyên và phát triển cảnh quan nhân văn bền vững.",
  focusArea: "Di sản văn hóa & Cảnh quan nhân văn",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cultural_heritage_sustainable_env.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cultural_heritage_sustainable_env.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/cultural_heritage_sustainable_env.png-wvc-sizes",
    alt: "Bảo tồn di sản văn hóa gắn liền với phát triển bền vững môi trường sinh thái"
  }
}];
export default function Units() {
  const prefersReduced = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReduced ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="các-đơn-vị-nghiên-cứu-trực-thuộc" data-nav="light" className="relative bg-background py-16 text-foreground md:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block with Dual-Rule Institutional Motif */}
        <div className="mb-12 max-w-3xl lg:mb-16">
          <div className="mb-3 inline-flex items-center gap-2 rounded-sm border border-border bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Building2 className="size-3.5" />
            <span>Cơ cấu tổ chức nghiên cứu khoa học</span>
          </div>
          
          <h2 className="font-default text-2xl font-bold tracking-tight text-secondary sm:text-3xl lg:text-4xl">
            Các đơn vị nghiên cứu trực thuộc
          </h2>

          {/* Institutional Anchor Divider */}
          <div className="mt-4 mb-5 flex items-center">
            <div className="h-1 w-14 bg-secondary" />
            <div className="h-px flex-1 bg-border" />
          </div>

          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Hệ thống các viện và trung tâm nghiên cứu chuyên sâu trực thuộc Hội Địa lý môi trường Việt Nam, thực hiện nhiệm vụ khoa học, chuyển giao công nghệ và dịch vụ chuyên môn theo quy định pháp luật.
          </p>
        </div>

        {/* Registry List of Affiliated Units */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="space-y-8 lg:space-y-10">
          {researchUnits.map((unit, index) => <motion.div key={unit.id} data-index={index} variants={itemVariants}>
              <Card className="group relative overflow-hidden rounded-sm border border-border bg-card p-0 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                {/* Top Institutional Green Rule Accent */}
                <div className="h-1 w-full bg-primary" />

                <div className="grid grid-cols-1 lg:grid-cols-12">
                  {/* Documentary Photograph Container */}
                  <div className="relative overflow-hidden bg-muted lg:col-span-5">
                    <div className="aspect-16/10 w-full sm:aspect-16/9 lg:h-full lg:min-h-[320px]">
                      <img src={unit.image.src} data-wvc-srcset={unit.image.srcset} data-wvc-sizes={unit.image.sizes} alt={unit.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1.5 rounded-sm bg-primary/95 px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-xs backdrop-blur-xs">
                        <BookOpen className="size-3" />
                        {unit.focusArea}
                      </span>
                    </div>
                  </div>

                  {/* Institution Details & Administrative Credentials */}
                  <div className="flex flex-col justify-between p-6 sm:p-8 lg:col-span-7">
                    <div className="space-y-4">
                      {/* Name of Institution */}
                      <h3 className="font-default text-xl font-bold leading-snug text-foreground sm:text-2xl">
                        {unit.name}
                      </h3>

                      {/* Leadership & Legal Authority Placards */}
                      <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
                        {/* Director / Head */}
                        <div className="flex items-start gap-3 rounded-sm border border-border/80 bg-background/60 p-3">
                          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-sm bg-accent text-primary">
                            <UserCheck className="size-4" />
                          </div>
                          <div>
                            <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {unit.leaderTitle}
                            </span>
                            <span className="font-default text-sm font-semibold text-foreground">
                              {unit.leaderName}
                            </span>
                          </div>
                        </div>

                        {/* Science & Tech Registry Code */}
                        <div className="flex items-start gap-3 rounded-sm border border-border/80 bg-background/60 p-3">
                          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-sm bg-accent text-secondary">
                            <Award className="size-4" />
                          </div>
                          <div>
                            <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Đăng ký KH&amp;CN
                            </span>
                            <span className="font-mono text-sm font-bold text-foreground">
                              {unit.regCode}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Official Establishment Decision */}
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground sm:text-sm">
                        <SafeHydrate><Calendar className="size-4 shrink-0 text-primary" /></SafeHydrate>
                        <span className="text-foreground/90">
                          {unit.legalDecision}, mã đăng ký {unit.regCode}
                        </span>
                      </div>

                      {/* Operational Overview Description */}
                      <div className="pt-2">
                        <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {unit.description}
                        </p>
                      </div>
                    </div>

                    {/* Verification Footer Indicator */}
                    <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <FileText className="size-3.5 text-primary" />
                        <span>Hồ sơ pháp lý lưu trữ tại Văn phòng Hội</span>
                      </div>
                      <span className="font-mono text-[11px] uppercase tracking-wider text-primary/80 font-medium">
                        Cơ quan chủ quản: VAEG
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}